import type {
  LogContext,
  Logger,
  LoggerAdapter,
  LoggerOptions,
  LogLevel,
} from "../types.ts";

const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  fatal: 4,
};

interface ConsoleAdapterOptions {
  timestamps?: boolean;
  json?: boolean;
}

function createConsoleLogger(
  options: LoggerOptions,
  adapterOptions: ConsoleAdapterOptions,
  parentContext?: LogContext
): Logger {
  const { name, level = "debug", defaultContext = {} } = options;
  const { timestamps = false, json = false } = adapterOptions;
  const minLevel = LOG_LEVEL_PRIORITY[level];
  const mergedContext = { ...parentContext, ...defaultContext };

  const shouldLog = (logLevel: LogLevel): boolean =>
    LOG_LEVEL_PRIORITY[logLevel] >= minLevel;

  const formatMessage = (
    logLevel: LogLevel,
    msg: string,
    context?: LogContext | Error
  ): [string, ...unknown[]] => {
    const timestamp = timestamps ? new Date().toISOString() : undefined;
    const prefix = timestamp ? `${timestamp} [${name}]` : `[${name}]`;

    if (json) {
      const logObject = {
        ...(timestamp && { timestamp }),
        level: logLevel,
        name,
        msg,
        ...mergedContext,
        ...(context instanceof Error
          ? { error: { message: context.message, stack: context.stack } }
          : context),
      };
      return [JSON.stringify(logObject)];
    }

    const contextToLog =
      context instanceof Error
        ? { error: context.message, ...mergedContext }
        : { ...mergedContext, ...context };

    const hasContext = Object.keys(contextToLog).length > 0;

    if (hasContext) {
      return [`${prefix} ${msg}`, contextToLog];
    }
    return [`${prefix} ${msg}`];
  };

  const log = (
    logLevel: LogLevel,
    msg: string,
    context?: LogContext | Error
  ): void => {
    if (!shouldLog(logLevel)) {
      return;
    }

    const args = formatMessage(logLevel, msg, context);

    // biome-ignore lint/style/useDefaultSwitchClause: all cases handled
    switch (logLevel) {
      case "debug":
        // biome-ignore lint/suspicious/noConsole: console adapter
        console.debug(...args);
        break;
      case "info":
        // biome-ignore lint/suspicious/noConsole: console adapter
        console.info(...args);
        break;
      case "warn":
        // biome-ignore lint/suspicious/noConsole: console adapter
        console.warn(...args);
        break;
      case "error":
      case "fatal":
        // biome-ignore lint/suspicious/noConsole: console adapter
        console.error(...args);
        if (context instanceof Error && context.stack) {
          // biome-ignore lint/suspicious/noConsole: console adapter
          console.error(context.stack);
        }
        break;
    }
  };

  return {
    name,
    debug: (msg, context) => log("debug", msg, context),
    info: (msg, context) => log("info", msg, context),
    warn: (msg, context) => log("warn", msg, context),
    error: (msg, context) => log("error", msg, context),
    fatal: (msg, context) => log("fatal", msg, context),
    child: (childContext) =>
      createConsoleLogger({ ...options, defaultContext: {} }, adapterOptions, {
        ...mergedContext,
        ...childContext,
      }),
  };
}

/** Create a console-based logger adapter. Works in any JS runtime. */
export function createConsoleAdapter(
  options: ConsoleAdapterOptions = {}
): LoggerAdapter {
  return {
    createLogger: (loggerOptions) =>
      createConsoleLogger(loggerOptions, options),
  };
}
